'use client';

/*
Hall of Fame — programme de fidélité. Mise en page alignée sur la homepage v5 :
fond blanc, titres Unbounded, listes à filets, paliers en rangée, final sur photo.
*/
import Image from 'next/image';
import Head from '@/core/head';
import { lang, hocT } from '@/helpers/translate';
import { getCanonicalUrl } from '@/helpers/router.js';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import { tierIconsCompact } from '@/components/icons/hall-of-fame-tiers';
import {
  SECTION, CARD, BTN_PRIMARY, H2, Kicker, ArrowIcon, NumberedList, FinalBand, kicker,
} from '@/core/editorial';
import LegendPlusOffers from '@/components/corpo/legendPlusOffers';

const BOOKING_URL = 'https://calendar.app.google/rdTTW3Jkf7xyJyJfA';

const TIERS = [
  { key: 'scout', perkCount: 2, threshold: 'Dès 5 réservations / an' },
  { key: 'blockbuster', perkCount: 4, threshold: 'Dès 10 réservations / an' },
  { key: 'legend', perkCount: 5, threshold: 'Dès 30 réservations / an' },
];

export default hocT(({ t }) => {
  const langCurrent = lang();
  const canurls = getCanonicalUrl('hall-of-fame', langCurrent);

  const advantages = [1, 2, 3, 4].map((i) => ({
    title: t(`halloffame:advantages.adv_${i}.title`),
    body: t(`halloffame:advantages.adv_${i}.desc`),
  }));

  const details = [1, 2, 3].map((i) => ({
    text: t(`halloffame:detail.item_${i}_text`),
    highlight: t(`halloffame:detail.item_${i}_highlight`),
  }));

  return (
    <div className="bg-white font-display text-was-ink">
      <Head>
        {canurls.map((cprops) => <link key={JSON.stringify(cprops)} {...cprops} />)}
      </Head>

      {/* Hero */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-10 px-6 pb-16 pt-28 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:px-8 lg:pb-24 lg:pt-36">
          <FadeUp>
            <Kicker n="01">{kicker(langCurrent, 'hof', 0)}</Kicker>
            <h1 className="mt-5 max-w-[640px] text-balance font-heading text-[32px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] md:text-[40px] lg:text-[46px]">
              {t('halloffame:hero.title_start')}
              {' '}
              <span className="text-was-violet">{t('halloffame:hero.title_highlight')}</span>
              {' '}
              {t('halloffame:hero.title_end')}
            </h1>
            <p className="mt-6 max-w-[520px] text-lg leading-relaxed text-was-slate">{t('halloffame:hero.desc')}</p>
            <div className="mt-8">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className={BTN_PRIMARY}>
                {t('halloffame:hero.cta')}
                <ArrowIcon />
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className={`relative aspect-[4/5] overflow-hidden ${CARD}`}>
            <Image src="/img/hall-of-fame-hero.png" alt={t('halloffame:hero.img_alt')} fill sizes="(max-width: 1024px) 100vw, 520px" className="object-cover" />
          </FadeUp>
        </div>
      </section>

      {/* Avantages */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="02">{kicker(langCurrent, 'hof', 1)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('halloffame:audience.title')}</h2>
            <p className="mt-5 max-w-[640px] text-base leading-relaxed text-was-slate">{t('halloffame:audience.text_1')}</p>
          </FadeUp>
          <div className="mt-12 lg:mt-16">
            <NumberedList items={advantages} columns={2} />
          </div>
          <p className="mt-6 text-[13px] text-was-muted">{t('halloffame:hero.note')}</p>
        </div>
      </section>

      {/* Paliers */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="03">{kicker(langCurrent, 'hof', 2)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('halloffame:tiers.subtitle')}</h2>
          </FadeUp>
          <Stagger stagger={0.08} delay={0.1} className="mt-12 grid border-y border-was-ink/8 md:grid-cols-3 lg:mt-16">
            {TIERS.map((tier, i) => (
              <StaggerChild key={tier.key} className={`flex flex-col py-8 md:px-8 ${i > 0 ? 'border-t border-was-ink/8 md:border-l md:border-t-0' : 'md:pl-0'}`}>
                <div className="flex items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-was-ink text-white [&>svg]:size-5" aria-hidden="true">{tierIconsCompact[tier.key]}</span>
                  <h3 className="m-0 font-heading text-[17px] font-extrabold uppercase leading-none tracking-[0.01em] text-was-ink">{t(`halloffame:tiers.${tier.key}.name`)}</h3>
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-heading text-[40px] font-extrabold leading-none tracking-[-0.01em] text-was-ink">{t(`halloffame:tiers.${tier.key}.discount`)}</span>
                  <span className="text-sm text-was-muted">{t('halloffame:tiers.discount_label')}</span>
                </div>
                <p className="mt-3 text-[15px] text-was-slate">{t(`halloffame:tiers.${tier.key}.tagline`)}</p>
                <ul className="m-0 mt-5 list-none space-y-2 border-t border-was-ink/8 p-0 pt-5 text-[14px] text-was-ink">
                  {Array.from({ length: tier.perkCount }, (_, p) => (
                    <li key={`${tier.key}perk${p}`} className="flex items-start gap-2.5">
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-was-ink" />
                      <span>{t(`halloffame:tiers.${tier.key}.perk_${p + 1}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 text-[13px] text-was-muted">{tier.threshold}</div>
              </StaggerChild>
            ))}
          </Stagger>
          <p className="mt-6 text-[13px] text-was-muted">{t('halloffame:tiers.disclaimer')}</p>
        </div>
      </section>

      {/* WEA-157 — Legend+ : l'abonnement se place juste apres les 3 paliers merites, comme le cran
          au-dessus. Prix lus en direct depuis Stripe (catalogue public).
          Bande sombre assumee : LegendPlusOffers est ecrit en blanc (cartes d'offres), il resterait
          illisible sur le fond blanc de la refonte v5. */}
      <section className="bg-was-abyss">
        <div className={SECTION}>
          <FadeUp>
            <h2 className="m-0 max-w-[760px] text-balance font-heading text-[26px] font-extrabold uppercase leading-[1.06] tracking-[-0.01em] text-white md:text-[34px]">
              {t('halloffame:legendPlus.title')}
            </h2>
            <p className="mt-5 max-w-[640px] text-base leading-relaxed text-white/70">
              {t('halloffame:legendPlus.subtitle')}
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-12 lg:mt-16">
            <LegendPlusOffers />
          </FadeUp>
        </div>
      </section>

      {/* Le détail qui compte */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <FadeUp>
              <Kicker n="04">{kicker(langCurrent, 'hof', 3)}</Kicker>
              <h2 className={`mt-5 text-balance ${H2}`}>{t('halloffame:detail.title')}</h2>
              <p className="mt-5 text-base leading-relaxed text-was-slate">
                <span className="font-medium text-was-ink">{t('halloffame:audience.text_2_highlight')}</span>
                {' '}
                {t('halloffame:audience.text_2')}
              </p>
            </FadeUp>
            <FadeUp delay={0.1} className={`p-7 lg:p-9 ${CARD}`}>
              <ul className="m-0 list-none space-y-5 p-0">
                {details.map((item) => (
                  <li key={item.highlight} className="flex items-start gap-3 text-[15px] leading-relaxed text-was-slate">
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-was-ink" />
                    <span>
                      {item.text}
                      {' '}
                      <span className="font-medium text-was-ink">{item.highlight}</span>
                      .
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-was-ink/8 pt-5 text-[13px] text-was-muted">{t('halloffame:advantages.note')}</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Final */}
      <FinalBand title={`${t('halloffame:cta.title_start')} ${t('halloffame:cta.title_highlight')} ?`}>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 text-base font-normal text-was-ink transition duration-200 hover:bg-was-violet hover:text-white">
          {t('halloffame:cta.btn_1')}
          <ArrowIcon />
        </a>
        <p className="w-full max-w-[520px] text-base text-white/75">{t('halloffame:cta.desc')}</p>
      </FinalBand>
    </div>
  );
});
